import { Button, Checkbox, Grid, MenuItem, OutlinedInput, Select, Stack, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { FC, forwardRef, useCallback, useEffect, useState } from "react";
import Modal, { CommonModalProps } from "..";
import { DbWorkflowUserTasksMetadataInfos } from "../../../Core/Api/Types/db-workflow-user-tasks-metadata-infos";
import { UserTaskMetadataType } from "../../../Core/Api/Types/user-task-metadata-type";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { DbWorkflowUserTasksInfos } from "../../../Core/Api/Types/db-workflow-user-tasks-infos";
import { workflowService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { produce } from "immer";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { useTranslation } from "react-i18next";

export type ModalCompleteUserTaskElementProps = {
  task: DbWorkflowUserTasksInfos;
  workflowId: number;
  onClose?: (success: boolean) => void;
};

export type ModalCompleteUserTaskProps = CommonModalProps<ModalCompleteUserTaskElementProps>;

const ModalCompleteUserTask = forwardRef<any, ModalCompleteUserTaskProps>((props, ref) => {
  const { task, workflowId, closeModal, onClose } = props;
  const enqueueSnackbar = useCustomSnackbar();
  const [taskLocal, setTaskLocal] = useState(task);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const handleMetadataChange = (value: string, index: number) => {
    setTaskLocal(
      produce((draft) => {
        draft.metadata![index].value = value;
      })
    );
  };

  const handleUpdate = useCallback(() => {
    setLoading(true);
    workflowService.UpdateUserTaskMetadata(taskLocal, workflowId).then((response) => {
      if (response.success) enqueueSnackbar(t("alert:success.taskUpdated"), "success");
      else enqueueSnackbar(t("alert:error.updatingTask", { message: response.message }), "error");

      setLoading(false);
      onClose && onClose(response.success!);

      closeModal();
    });
  }, [taskLocal, workflowId]);

  const handleUpdateAndComplete = useCallback(() => {
    setLoading(true);
    workflowService.CompleteUserTaskMetadata(taskLocal, workflowId).then((response) => {
      if (response.success) enqueueSnackbar(t("alert:success.taskCompleted"), "success");
      else enqueueSnackbar(t("alert:error.completingTask", { message: response.message }), "error");

      setLoading(false);
      onClose && onClose(response.success!);

      closeModal();
    });
  }, [taskLocal, workflowId]);

  const handleSaveMeta = useCallback(() => {
    onClose && onClose(true);
    closeModal();
  }, []);

  useEffect(() => {
    if (!taskLocal.metadata) return;

    for (let i = 0; i < taskLocal.metadata!.length; ++i) {
      if (taskLocal.metadata[i].type === UserTaskMetadataType.checkbox && taskLocal.metadata[i].value === null)
        setTaskLocal(
          produce((draft) => {
            draft.metadata![i].value = "false";
          })
        );
    }
  }, [taskLocal.metadata]);

  return (
    <Modal
      reference={ref}
      title={t("modal:title.completeUserTask")}
      closeModal={closeModal}
      width={704}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={closeModal}>{t("common:cancel")}</Button>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" color="primary" onClick={handleSaveMeta}>
              {t("button:saveMetadatOnly")}
            </Button>
            <Button variant="outlined" color="primary" onClick={handleUpdate} loading={loading}>
              {t("button:updateWorkflow")}
            </Button>
            <Button variant="outlined" color="success" onClick={handleUpdateAndComplete} loading={loading}>
              {t("button:updateAndCompleteWorkflow")}
            </Button>
          </Stack>
        </Stack>
      }
    >
      {taskLocal.metadata && taskLocal.metadata.length ? (
        <Grid container alignItems="center" spacing={2}>
          {taskLocal.metadata.map((item, index) => (
            <MetadataField
              key={index}
              label={item.key}
              options={item.options}
              type={item.type}
              onChange={(value: string) => handleMetadataChange(value, index)}
            />
          ))}
        </Grid>
      ) : (
        t("modal:modalCompleteUserTask.body.noMetadataToUpdate")
      )}
    </Modal>
  );
});

export default ModalCompleteUserTask;

interface MetadataFieldProps {
  label: DbWorkflowUserTasksMetadataInfos["key"];
  options: DbWorkflowUserTasksMetadataInfos["options"];
  type: DbWorkflowUserTasksMetadataInfos["type"];
  onChange: (value: string) => void;
}

const MetadataField: FC<MetadataFieldProps> = (props) => {
  const { label, options, type, onChange } = props;
  const { t } = useTranslation();

  return (
    <>
      {type === UserTaskMetadataType.checkbox && (
        <>
          <Grid item xs={2}>
            <Typography fontWeight={500} color="text.secondary">
              {label}
            </Typography>
          </Grid>
          <Grid item xs={10}>
            <Checkbox onChange={(event, checked) => (checked ? onChange("true") : onChange("false"))} />
          </Grid>
        </>
      )}
      {type === UserTaskMetadataType.datePicker && (
        <>
          <Grid item xs={2}>
            <Typography fontWeight={500} color="text.secondary">
              {label}
            </Typography>
          </Grid>
          <Grid item xs={10}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format={GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS}
                onChange={(value) => onChange(value?.toString() ?? "")}
              />
            </LocalizationProvider>
          </Grid>
        </>
      )}
      {type === UserTaskMetadataType.select && (
        <>
          <Grid item xs={2}>
            <Typography fontWeight={500} color="text.secondary">
              {label}
            </Typography>
          </Grid>
          <Grid item xs={10}>
            <Select
              displayEmpty
              IconComponent={ExpandMoreRoundedIcon}
              inputProps={{ "aria-label": "Without label" }}
              onChange={(event) => onChange(event.target.value as string)}
              fullWidth={true}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            >
              {options?.map((option, index) => (
                <MenuItem key={index} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </>
      )}

      {type === UserTaskMetadataType.textarea && (
        <>
          <Grid item xs={2}>
            <Typography fontWeight={500} color="text.secondary">
              {label}
            </Typography>
          </Grid>
          <Grid item xs={10}>
            <OutlinedInput multiline onChange={(event) => onChange(event.target.value)} fullWidth={true} />
          </Grid>
        </>
      )}

      {type !== UserTaskMetadataType.checkbox &&
        type !== UserTaskMetadataType.datePicker &&
        type !== UserTaskMetadataType.select &&
        type !== UserTaskMetadataType.textarea &&
        t("modal:modalCompleteUserTask.body.metadataIsNotSupported", { type })}
    </>
  );
};
